import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Switch } from "./components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "./components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "./components/ui/alert";
import { Loader2, AlertCircle, CheckCircle, Database, Download, BarChart3, Upload, FileText, XCircle, RefreshCw, LogOut, User, Mail, Save, Send, Shield, Key, Lock, Filter, Settings, Building2, Eye, EyeOff, X, Activity, Ban, Trash2, Clock, Play } from "lucide-react";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { BACKEND_URL } from './config';
import SecretsSection from './admin/sections/SecretsSection';
import SchedulerSection from './admin/sections/SchedulerSection';
import BrowserProviderSection from './admin/sections/BrowserProviderSection';
import NegativeListSection from './admin/sections/NegativeListSection';
import AdminCredentialsSection from './admin/sections/AdminCredentialsSection';

// Emergency Stop Button Component
const EmergencyStopButton = () => {
  const [isKilling, setIsKilling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleEmergencyStop = async () => {
    setIsKilling(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/force-stop-all`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (data.status === 'success') {
        toast.success(`Emergency stop successful! Killed ${data.killed_processes?.length || 0} processes`);
        
        // Show details if there were killed processes
        if (data.killed_processes && data.killed_processes.length > 0) {
          console.log('Killed processes:', data.killed_processes);
        }
        if (data.errors && data.errors.length > 0) {
          console.warn('Errors during stop:', data.errors);
        }
      } else {
        toast.error('Emergency stop failed: ' + (data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Emergency stop error:', error);
      toast.error('Failed to execute emergency stop');
    } finally {
      setIsKilling(false);
      setShowConfirm(false);
    }
  };

  return (
    <div className="space-y-2">
      {!showConfirm ? (
        <Button
          onClick={() => setShowConfirm(true)}
          variant="outline"
          size="sm"
          className="w-full border-red-500 text-red-600 hover:bg-red-50 hover:border-red-600 font-semibold"
        >
          <AlertCircle className="w-4 h-4 mr-2" />
          🚨 Emergency Stop
        </Button>
      ) : (
        <div className="space-y-2 p-2 bg-red-50 rounded border border-red-200">
          <p className="text-xs text-red-700 font-medium">
            This will force-kill ALL scraper processes and browsers. Are you sure?
          </p>
          <div className="flex gap-2">
            <Button
              onClick={handleEmergencyStop}
              disabled={isKilling}
              size="sm"
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              {isKilling ? (
                <>
                  <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                  Killing...
                </>
              ) : (
                'Yes, Kill All'
              )}
            </Button>
            <Button
              onClick={() => setShowConfirm(false)}
              disabled={isKilling}
              variant="outline"
              size="sm"
              className="flex-1"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
      <p className="text-xs text-slate-500">
        Use when normal stop doesn't work
      </p>
    </div>
  );
};

// Backend Logs Monitor Component
const BackendLogsMonitor = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [filterType, setFilterType] = useState('all');

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/backend-logs?lines=200&filter=${filterType}`);
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to fetch logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [filterType]);

  useEffect(() => {
    let interval;
    if (autoRefresh) {
      interval = setInterval(fetchLogs, 5000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh, filterType]);

  const downloadLogs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/export-scrape-logs`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `backend_logs_${new Date().toISOString()}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Logs downloaded successfully');
    } catch (error) {
      console.error('Error downloading logs:', error);
      toast.error('Failed to download logs');
    }
  };

  const getLogColor = (logText) => {
    if (logText.includes('ERROR') || logText.includes('🔴')) return 'text-red-600';
    if (logText.includes('WARNING') || logText.includes('⚠️')) return 'text-yellow-600';
    if (logText.includes('SUCCESS') || logText.includes('✅')) return 'text-green-600';
    if (logText.includes('CLEANUP') || logText.includes('🔄')) return 'text-blue-600';
    if (logText.includes('💾') || logText.includes('Memory')) return 'text-purple-600';
    return 'text-gray-700';
  };

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <div>
              <CardTitle>Backend Logs Monitoring</CardTitle>
              <CardDescription>Real-time monitoring of memory usage, cleanup operations, and performance metrics</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant={autoRefresh ? 'default' : 'outline'}
                onClick={() => setAutoRefresh(!autoRefresh)}
                className="flex items-center gap-2"
                size="sm"
              >
                <RefreshCw className={`w-4 h-4 ${autoRefresh ? 'animate-spin' : ''}`} />
                {autoRefresh ? 'Auto (5s)' : 'Auto Off'}
              </Button>
              <Button onClick={fetchLogs} disabled={loading} variant="outline" size="sm">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
              <Button onClick={downloadLogs} variant="outline" size="sm">
                <Download className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter Buttons */}
          <div className="flex gap-2 flex-wrap mb-4">
            {[
              { value: 'all', label: 'All Logs', icon: '📋' },
              { value: 'memory', label: 'Memory', icon: '💾' },
              { value: 'cleanup', label: 'Cleanup', icon: '🔄' },
              { value: 'degradation', label: 'Degradation', icon: '🔴' },
              { value: 'timing', label: 'Timing', icon: '⏱️' },
            ].map((filter) => (
              <Button
                key={filter.value}
                variant={filterType === filter.value ? 'default' : 'outline'}
                onClick={() => setFilterType(filter.value)}
                size="sm"
              >
                {filter.icon} {filter.label}
              </Button>
            ))}
          </div>

          {/* Logs Display */}
          <div className="bg-slate-900 text-slate-100 p-4 rounded-lg h-[500px] overflow-y-auto font-mono text-xs">
            {logs.length === 0 ? (
              <div className="text-slate-400 text-center py-8">No logs available</div>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} className={`py-0.5 ${getLogColor(log)}`}>
                  {log}
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Log Location</CardTitle>
          </CardHeader>
          <CardContent>
            <code className="text-xs bg-slate-100 p-2 rounded block break-all">
              /var/log/supervisor/backend.err.log
            </code>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Quick Commands</CardTitle>
          </CardHeader>
          <CardContent className="space-y-1">
            <code className="text-xs bg-slate-100 p-1 rounded block">tail -n 100 backend.err.log</code>
            <code className="text-xs bg-slate-100 p-1 rounded block">grep "Memory" backend.err.log</code>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Status</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-2">
              {autoRefresh ? (
                <>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span className="text-sm text-green-600">Active</span>
                </>
              ) : (
                <>
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                  <span className="text-sm text-gray-600">Inactive</span>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

const AdminScraper = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('scraper'); // 'scraper', 'filter', 'email-recaptcha', 'amc', 'admin-creds', 'performance'
  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [error, setError] = useState(null);
  const [batchResult, setBatchResult] = useState(null);
  const [csvFile, setCsvFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [loadingPerformance, setLoadingPerformance] = useState(false);
  const [scrapingProgress, setScrapingProgress] = useState(null);
  const [isScrapingActive, setIsScrapingActive] = useState(false);
  const [progressInterval, setProgressInterval] = useState(null);
  const [heartbeatInterval, setHeartbeatInterval] = useState(null);
  const [heartbeatActive, setHeartbeatActive] = useState(false);
  const [stoppingScrap, setStoppingScrap] = useState(false);
  const [refreshingData, setRefreshingData] = useState(false);
  const [dataStats, setDataStats] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(() => {
    // Load auto-refresh state from localStorage
    const saved = localStorage.getItem('autoRefreshEnabled');
    return saved === 'true';
  });
  const [refreshing, setRefreshing] = useState(false);

  // Email configuration states
  const [emailConfig, setEmailConfig] = useState({
    configured: false,
    sender_email: '',
    has_password: false
  });
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [testEmail, setTestEmail] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);

  // Filter configuration states
  const [minAum, setMinAum] = useState(0);
  
  // Zero Criteria Filter state
  const [zeroCriteriaEnabled, setZeroCriteriaEnabled] = useState(false);
  const [zeroCriteriaPeriod, setZeroCriteriaPeriod] = useState(null);
  const [newMinAum, setNewMinAum] = useState('');
  const [savingFilter, setSavingFilter] = useState(false);

  // AMC Master CSV states
  const [amcCsvFile, setAmcCsvFile] = useState(null);
  const [uploadingCsv, setUploadingCsv] = useState(false);
  const [csvUploadResult, setCsvUploadResult] = useState(null);

  // Admin credentials states

  // AMC Details section states
  const [availableAmcs, setAvailableAmcs] = useState([]);
  const [selectedAmcForDetails, setSelectedAmcForDetails] = useState('');
  const [amcDetailsSchemes, setAmcDetailsSchemes] = useState([]);
  const [loadingAmcDetails, setLoadingAmcDetails] = useState(false);
  const [amcDetailsCsvFile, setAmcDetailsCsvFile] = useState(null);
  const [uploadingAmcDetails, setUploadingAmcDetails] = useState(false);

  // Performance Metrics states
  const [performanceMetrics, setPerformanceMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);
  const [expandedPM, setExpandedPM] = useState(null);
  const [showAmcSchemesModal, setShowAmcSchemesModal] = useState(false);
  const [amcSchemeFilter, setAmcSchemeFilter] = useState('all'); // 'all', 'pms', 'aif'
  const [amcAifCsvFile, setAmcAifCsvFile] = useState(null);
  const [uploadingAmcAif, setUploadingAmcAif] = useState(false);
  
  // Browser status
  const [browserStatus, setBrowserStatus] = useState({
    ready: false,
    checking: true,
    message: 'Checking browser installation...'
  });
  
  // Force rescrape flag
  const [forceRescrape, setForceRescrape] = useState(false);

  // Save auto-refresh state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('autoRefreshEnabled', autoRefresh);
  }, [autoRefresh]);

  const sendHeartbeat = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/heartbeat`);
      setHeartbeatActive(true);
      setTimeout(() => setHeartbeatActive(false), 500); // Flash indicator
    } catch (err) {
      console.error('Heartbeat error:', err);
    }
  };

  const startHeartbeat = () => {
    // Send heartbeat every 15 seconds (more frequent to keep session alive)
    const interval = setInterval(sendHeartbeat, 15000);
    setHeartbeatInterval(interval);
    sendHeartbeat(); // Send immediately
  };

  const stopHeartbeat = () => {
    if (heartbeatInterval) {
      clearInterval(heartbeatInterval);
      setHeartbeatInterval(null);
    }
  };

  const fetchProgress = async () => {
    try {
      // Add cache-busting parameter to prevent stale responses
      const timestamp = Date.now();
      const response = await fetch(`${BACKEND_URL}/api/scraping-progress?_t=${timestamp}`, {
        cache: 'no-store',
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        console.error('Failed to fetch progress:', response.status);
        return; // Keep existing scrapingProgress, don't update to null
      }
      
      const data = await response.json();
      
      // Only update if we got valid data
      if (data && typeof data === 'object') {
        // Track if scraping is actually running
        if (data.in_progress) {
          setIsScrapingActive(true);
          setScrapingProgress(data);
        } else if (data.status === 'completed' || data.status === 'stopped' || data.status === 'stopping' || data.status === 'idle') {
          // Only update if we were previously scraping (prevents false updates)
          if (isScrapingActive) {
            // Ensure final progress shows correct count
            setScrapingProgress(data);
            setIsScrapingActive(false);
          }
        }
        // Don't update if backend is idle and we're not actively scraping - prevents flickering
      }
      
      // Stop polling if scraping is complete
      if (!data.in_progress && progressInterval) {
        clearInterval(progressInterval);
        setProgressInterval(null);
        stopHeartbeat();
        setUploadLoading(false);
        
        // Ensure we capture the final progress state before clearing
        setScrapingProgress(data);
        
        // Update upload status to show completion
        if (data.status === 'stopped' || data.stop_requested) {
          setUploadStatus({ 
            step: 'complete', 
            message: '⏹️ Scraping stopped by user' 
          });
        } else if (data.status === 'completed' || data.status === 'idle') {
          setUploadStatus({ 
            step: 'complete', 
            message: `✅ Scraping completed! (${data.successful || 0} scraped, ${data.skipped || 0} skipped)` 
          });
        } else {
          setUploadStatus({ 
            step: 'complete', 
            message: '✅ Scraping finished' 
          });
        }
        
        // Clear progress display after 10 seconds when scraping completes
        setTimeout(() => {
          setUploadStatus(null);
          setScrapingProgress(null);
        }, 10000);
      }
    } catch (err) {
      console.error('Error fetching progress:', err);
      // Don't clear scrapingProgress on error - keep showing last known state
    }
  };
  
  const fetchBrowserStatus = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/browser-status`, {
        cache: 'no-store'
      });
      const data = await response.json();
      setBrowserStatus(data);
    } catch (err) {
      console.error('Error fetching browser status:', err);
    }
  };

  const startProgressPolling = () => {
    // Poll every 2 seconds
    const interval = setInterval(fetchProgress, 2000);
    setProgressInterval(interval);
    startHeartbeat(); // Start heartbeat when polling starts
  };

  const stopProgressPolling = () => {
    if (progressInterval) {
      clearInterval(progressInterval);
      setProgressInterval(null);
    }
    stopHeartbeat();
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
    } else {
      setError('Please upload a valid CSV file');
      setCsvFile(null);
    }
  };

  const handleUploadAndScrape = async () => {
    if (!csvFile) {
      setError('Please select a CSV file first');
      return;
    }

    setUploadLoading(true);
    setBatchResult(null);
    setError(null);
    setUploadStatus({ step: 'reading', message: 'Reading CSV file...' });

    try {
      const formData = new FormData();
      formData.append('file', csvFile);
      
      // Build URL with query parameters
      let url = `${BACKEND_URL}/api/scrape-batch-csv`;
      const params = new URLSearchParams();
      if (year) params.append('year', year);
      if (month) params.append('month', month);
      params.append('force_rescrape', forceRescrape);
      if (params.toString()) {
        url += '?' + params.toString();
      }

      setUploadStatus({ step: 'uploading', message: 'Uploading CSV to server...' });

      setUploadStatus({ step: 'processing', message: 'Processing and scraping data from SEBI website...' });
      
      // Start polling for progress
      startProgressPolling();

      // Set a long timeout since scraping takes time
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 10 minute timeout

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const data = await response.json();

      if (response.ok) {
        // Check if scraping actually started (background task) or completed immediately
        if (data.status === 'started') {
          // Scraping is running in background - keep polling
          setUploadStatus({ 
            step: 'processing', 
            message: `✓ Found ${data.total_attempted || 0} Portfolio Managers. Scraping in progress...` 
          });
          // Initialize progress state immediately to show progress bar
          setIsScrapingActive(true);
          setScrapingProgress({
            in_progress: true,
            current_pm: null,
            current_index: 0,
            total_pms: data.total_attempted || 0,
            completed: [],
            status: 'starting'
          });
          // Progress polling already started above, so just continue
        } else {
          // Scraping completed immediately (e.g., all already scraped)
          stopProgressPolling();
          setUploadStatus({ 
            step: 'complete', 
            message: `✓ Found ${data.total_attempted || 0} Portfolio Managers. Scraping complete!` 
          });
          setBatchResult(data);
          setCsvFile(null);
          // Reset file input
          document.getElementById('csvFile').value = '';
        }
      } else {
        stopProgressPolling();
        setUploadStatus(null);
        setError(data.message || 'Failed to process CSV and scrape data');
      }
    } catch (err) {
      stopProgressPolling();
      setUploadStatus(null);
      setError(`Error: ${err.message}`);
    } finally {
      setUploadLoading(false);
    }
  };

  const fetchPerformanceData = async () => {
    setLoadingPerformance(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/performance-data`);
      const data = await response.json();
      setPerformanceData(data);
    } catch (err) {
      console.error('Error fetching performance data:', err);
    } finally {
      setLoadingPerformance(false);
    }
  };

  const handleClearCacheAndRefresh = async () => {
    try {
      setRefreshingData(true);
      
      // Clear backend cache
      const response = await fetch(`${BACKEND_URL}/api/clear-cache`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (response.ok) {
        // Clear frontend sessionStorage
        sessionStorage.clear();
        
        // Show success message
        toast.success('✅ Cache cleared! Refreshing data...');
        
        // Wait a moment then reload the page to get fresh data
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      } else {
        toast.error(data.message || 'Failed to clear cache');
        setRefreshingData(false);
      }
    } catch (err) {
      toast.error(`Error clearing cache: ${err.message}`);
      setRefreshingData(false);
    }
  };

  const handleDownloadPerformanceCSV = () => {
    const url = `${BACKEND_URL}/api/export-performance-csv`;
    console.log('Downloading CSV from:', url);
    
    // Create a temporary link and click it
    const link = document.createElement('a');
    link.href = url;
    link.download = 'performance_data.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadSuccessfulPMs = () => {
    const url = `${BACKEND_URL}/api/download-successful-pms`;
    console.log('Downloading successful PMs Excel from:', url);
    
    // Create a temporary link and click it
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Downloading successful PMs Excel file...');
  };

  const handleDownloadFailedPMs = () => {
    const url = `${BACKEND_URL}/api/download-failed-pms`;
    console.log('Downloading failed PMs Excel from:', url);
    
    // Create a temporary link and click it
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Downloading failed PMs Excel file...');
  };

  const handleDownloadAMCContacts = () => {
    const url = `${BACKEND_URL}/api/download-amc-contacts`;
    console.log('Downloading AMC contacts Excel from:', url);
    
    // Create a temporary link and click it
    const link = document.createElement('a');
    link.href = url;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    toast.success('Downloading AMC contacts Excel file...');
  };


  const handleClearCache = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/clear-cache`, {
        method: 'POST'
      });
      const result = await response.json();
      
      if (result.status === 'success') {
        toast.success('Cache cleared successfully! Users will see fresh data.');
      } else {
        toast.error('Failed to clear cache');
      }
    } catch (error) {
      console.error('Error clearing cache:', error);
      toast.error('Error clearing cache');
    }
  };


  const handleDownloadSampleCSV = () => {
    const csvContent = `Portfolio Manager
2POINT2 CAPITAL ADVISORS LLP
360 ONE ASSET MANAGEMENT LIMITED
ALCHEMY CAPITAL MANAGEMENT PRIVATE LTD
AMBIT CAPITAL PRIVATE LTD
AXIS ASSET MANAGEMENT COMPANY LIMITED`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sample_portfolio_managers.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadScrapeLogs = async () => {
    try {
      toast.info('Preparing scrape logs download...');
      const response = await fetch(`${BACKEND_URL}/api/export-scrape-logs`);
      
      if (!response.ok) {
        throw new Error('Failed to download scrape logs');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'scrape_logs.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Scrape logs downloaded successfully!');
    } catch (error) {
      console.error('Error downloading scrape logs:', error);
      toast.error('Failed to download scrape logs');
    }
  };

  // Fetch data statistics
  const fetchDataStats = async () => {
    try {
      setRefreshing(true);
      const response = await fetch(`${BACKEND_URL}/api/performance-data-display`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setDataStats({
          totalRecords: result.total_records,
          totalSchemesFound: result.total_schemes_found,
          filteredOutAum: result.filtered_out_aum,
          filteredOut3Year: result.filtered_out_3year,
          totalPmsProcessed: result.total_pms_processed
        });
        setLastUpdated(new Date());
        toast.success("Data statistics refreshed!");
      }
    } catch (error) {
      console.error('Error fetching data stats:', error);
      toast.error("Failed to refresh data statistics");
    } finally {
      setRefreshing(false);
    }
  };

  // Fetch performance metrics
  const fetchPerformanceMetrics = async (limit = 50) => {
    try {
      setLoadingMetrics(true);
      const response = await fetch(`${BACKEND_URL}/api/performance-metrics?limit=${limit}`);
      const result = await response.json();
      
      if (result.status === 'success') {
        setPerformanceMetrics(result);
        toast.success(`Loaded ${result.total_records} recent scrapes`);
      } else {
        toast.error("Failed to fetch performance metrics");
      }
    } catch (error) {
      console.error('Error fetching performance metrics:', error);
      toast.error("Failed to fetch performance metrics");
    } finally {
      setLoadingMetrics(false);
    }
  };

  // Filter configuration handlers
  const fetchFilterConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/filter-config`);
      const data = await response.json();
      if (data.status === 'success') {
        setMinAum(data.min_aum);
        setNewMinAum(data.min_aum.toString());
      }
    } catch (error) {
      console.error('Error fetching filter config:', error);
    }
  };

  const handleSaveFilterConfig = async () => {
    const aumValue = parseFloat(newMinAum);
    if (isNaN(aumValue) || aumValue < 0) {
      toast.error('Please enter a valid AUM value (0 or greater)');
      return;
    }

    setSavingFilter(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/filter-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ min_aum: aumValue })
      });

      const data = await response.json();
      if (data.status === 'success') {
        toast.success(data.message || 'Filter updated successfully!');
        setMinAum(aumValue);
        // Refresh data to show filtered results
        fetchDataStats();
      } else {
        toast.error(data.message || 'Failed to update filter');
      }
    } catch (error) {
      console.error('Error saving filter config:', error);
      toast.error('Failed to update filter configuration');
    } finally {
      setSavingFilter(false);
    }
  };

  // Zero Criteria Filter handlers
  const fetchZeroCriteriaConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/zero-criteria-config`);
      const data = await response.json();
      setZeroCriteriaEnabled(data.enabled || false);
      setZeroCriteriaPeriod(data.period || null);
    } catch (error) {
      console.error('Error fetching zero criteria config:', error);
    }
  };

  const handleUpdateZeroCriteria = async (enabled, period) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/zero-criteria-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          enabled, 
          period: enabled ? period : null 
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        toast.success(data.message || 'Zero criteria filter updated!');
        setZeroCriteriaEnabled(enabled);
        setZeroCriteriaPeriod(enabled ? period : null);
      } else {
        toast.error(data.message || 'Failed to update zero criteria filter');
      }
    } catch (error) {
      console.error('Error updating zero criteria config:', error);
      toast.error('Failed to update zero criteria filter');
    }
  };

  // Email configuration handlers
  const fetchEmailConfig = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/email-config`);
      const data = await response.json();
      if (data.status === 'success') {
        setEmailConfig(data);
        if (data.configured) {
          setNewEmail(data.sender_email);
        }
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    }
  };

  const handleSaveEmailConfig = async () => {
    if (!newEmail || !newPassword) {
      toast.error('Please enter both email and password');
      return;
    }

    setSavingEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/email-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sender_email: newEmail,
          sender_password: newPassword
        })
      });

      const data = await response.json();
      if (data.status === 'success') {
        toast.success('Email configuration saved successfully!');
        setNewPassword(''); // Clear password field
        fetchEmailConfig(); // Refresh config
      } else {
        toast.error(data.message || 'Failed to save configuration');
      }
    } catch (error) {
      console.error('Error saving email config:', error);
      toast.error('Failed to save email configuration');
    } finally {
      setSavingEmail(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter a test recipient email');
      return;
    }

    setTestingEmail(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/test-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ test_recipient: testEmail })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Test email failed');
      }

      const data = await response.json();
      toast.success(data.message || 'Test email sent successfully!');
      setTestEmail(''); // Clear test email field
    } catch (error) {
      console.error('Error sending test email:', error);
      toast.error(error.message || 'Failed to send test email');
    } finally {
      setTestingEmail(false);
    }
  };





  // Handle CSV file selection
  const handleCsvFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setAmcCsvFile(file);
      setCsvUploadResult(null);
    }
  };

  // Upload AMC Master CSV
  const handleUploadAmcCsv = async () => {
    if (!amcCsvFile) {
      toast.error('Please select a CSV file first');
      return;
    }

    setUploadingCsv(true);
    setCsvUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', amcCsvFile);

      const response = await fetch(`${BACKEND_URL}/api/upload-amc-master-csv`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || 'CSV uploaded successfully');
        setCsvUploadResult(data);
        setAmcCsvFile(null);
        // Reset file input
        const fileInput = document.getElementById('amc-csv-input');
        if (fileInput) fileInput.value = '';
      } else {
        toast.error(data.detail || 'Failed to upload CSV');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error('Failed to upload CSV file');
    } finally {
      setUploadingCsv(false);
    }
  };

  // Download sample CSV
  const handleDownloadSampleCsv = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/download-sample-amc-csv`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'sample_amc_master.csv';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Sample CSV downloaded');
      } else {
        toast.error('Failed to download sample CSV');
      }
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      toast.error('Failed to download sample CSV');
    }
  };

  const handleDownloadCurrentAmcData = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/download-current-amc-data`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `amc_logins_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Current AMC data downloaded successfully');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to download AMC data');
      }
    } catch (error) {
      console.error('Error downloading current AMC data:', error);
      toast.error('Failed to download AMC data');
    }
  };

  const handleDownloadAmcSampleCsv = async () => {
    if (!selectedAmcForDetails) {
      toast.error('Please select an AMC first');
      return;
    }

    try {
      const url = `${BACKEND_URL}/api/download-amc-sample-csv?amc_name=${encodeURIComponent(selectedAmcForDetails)}`;
      const response = await fetch(url);
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${selectedAmcForDetails}_PMS_sample_template.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        toast.success('PMS Sample CSV downloaded successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to download sample CSV');
      }
    } catch (error) {
      console.error('Error downloading sample CSV:', error);
      toast.error('Failed to download sample CSV');
    }
  };

  const handleDownloadAifSampleCsv = async () => {
    if (!selectedAmcForDetails) {
      toast.error('Please select an AMC first');
      return;
    }

    try {
      const url = `${BACKEND_URL}/api/download-aif-sample-csv`;
      const response = await fetch(url);
      
      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${selectedAmcForDetails}_AIF_sample_template.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
        toast.success('AIF Sample CSV downloaded successfully!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Failed to download AIF sample CSV');
      }
    } catch (error) {
      console.error('Error downloading AIF sample CSV:', error);
      toast.error('Failed to download AIF sample CSV');
    }
  };

  // Fetch available AMCs for AMC Details section
  const fetchAvailableAmcs = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/available-amcs`);
      const data = await response.json();
      
      if (response.ok) {
        setAvailableAmcs(data.amcs || []);
      } else {
        console.error('Failed to fetch AMCs');
        setAvailableAmcs([]);
      }
    } catch (error) {
      console.error('Error fetching AMCs:', error);
      setAvailableAmcs([]);
    }
  };

  // Fetch schemes for selected AMC (AMC Details section)
  const fetchAmcDetailsSchemes = async (amcName) => {
    if (!amcName) return;
    
    setLoadingAmcDetails(true);
    try {
      console.log('Fetching schemes for:', amcName);
      
      const url = `${BACKEND_URL}/api/amc-schemes?amc_name=${encodeURIComponent(amcName)}`;
      console.log('Full URL:', url);
      
      const response = await fetch(url);
      const responseOk = response.ok;
      const responseStatus = response.status;
      
      console.log('Response status:', responseStatus);
      
      const data = await response.json();
      console.log('Response data:', data);
      
      if (responseOk) {
        const schemes = data.schemes || [];
        setAmcDetailsSchemes(schemes);
        console.log('Loaded schemes:', schemes.length);
        toast.success(`Loaded ${schemes.length} scheme(s) for ${amcName}`);
      } else {
        console.error('Response not OK:', responseStatus, data);
        toast.error(data.detail || 'Failed to load schemes');
        setAmcDetailsSchemes([]);
      }
    } catch (error) {
      console.error('Error fetching AMC schemes:', error);
      toast.error(`Failed to load schemes: ${error.message}`);
      setAmcDetailsSchemes([]);
    } finally {
      setLoadingAmcDetails(false);
    }
  };

  // Handle AMC selection change
  const handleAmcDetailsSelection = (amcName) => {
    setSelectedAmcForDetails(amcName);
    fetchAmcDetailsSchemes(amcName);
  };

  // Handle CSV file selection for AMC Details (PMS)
  const handleAmcDetailsCsvSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.name.endsWith('.csv')) {
        toast.error('Please select a CSV file');
        return;
      }
      setAmcDetailsCsvFile(file);
    }
  };

  // Upload CSV for selected AMC
  const handleAmcDetailsUploadCsv = async () => {
    if (!selectedAmcForDetails) {
      toast.error('Please select an AMC first');
      return;
    }

    if (!amcDetailsCsvFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setUploadingAmcDetails(true);
    try {
      const formData = new FormData();
      formData.append('file', amcDetailsCsvFile);
      formData.append('amc_name', selectedAmcForDetails);

      console.log('Uploading PMS CSV for AMC:', selectedAmcForDetails);
      console.log('File:', amcDetailsCsvFile.name, 'Size:', amcDetailsCsvFile.size);

      const response = await fetch(`${BACKEND_URL}/api/upload-amc-data`, {
        method: 'POST',
        body: formData
      });

      console.log('Response status:', response.status);
      
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 200)}`);
      }

      if (response.ok) {
        toast.success(data.message || 'PMS scheme data uploaded successfully');
        setAmcDetailsCsvFile(null);
        // Reset file input
        const fileInput = document.getElementById('amc-details-csv-input');
        if (fileInput) fileInput.value = '';
        // Refresh schemes
        fetchAmcDetailsSchemes(selectedAmcForDetails);
      } else {
        console.error('Upload failed:', data);
        toast.error(data.detail || data.message || 'Failed to upload CSV');
      }
    } catch (error) {
      console.error('Error uploading CSV:', error);
      toast.error(`Upload failed: ${error.message}`);
    } finally {
      setUploadingAmcDetails(false);
    }
  };

  // Handle AIF CSV file selection for AMC Details
  const handleAmcAifCsvSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAmcAifCsvFile(file);
      console.log('AIF CSV file selected:', file.name);
    }
  };

  // Handle AIF CSV upload for AMC Details
  const handleAmcAifUploadCsv = async () => {
    if (!selectedAmcForDetails) {
      toast.error('Please select an AMC first');
      return;
    }

    if (!amcAifCsvFile) {
      toast.error('Please select an AIF CSV file');
      return;
    }

    setUploadingAmcAif(true);
    try {
      const formData = new FormData();
      formData.append('file', amcAifCsvFile);
      formData.append('amc_name', selectedAmcForDetails);

      console.log('Uploading AIF CSV for AMC:', selectedAmcForDetails);
      console.log('File:', amcAifCsvFile.name, 'Size:', amcAifCsvFile.size);

      const response = await fetch(`${BACKEND_URL}/api/upload-aif-data`, {
        method: 'POST',
        body: formData
      });

      console.log('Response status:', response.status);
      
      let data;
      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text);
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 200)}`);
      }

      if (response.ok) {
        toast.success(data.message || 'AIF data uploaded successfully');
        setAmcAifCsvFile(null);
        // Reset file input
        const fileInput = document.getElementById('amc-aif-csv-input');
        if (fileInput) fileInput.value = '';
        // Refresh schemes
        fetchAmcDetailsSchemes(selectedAmcForDetails);
      } else {
        console.error('AIF upload failed:', data);
        toast.error(data.detail || data.message || 'Failed to upload AIF CSV');
      }
    } catch (error) {
      console.error('Error uploading AIF CSV:', error);
      toast.error(`AIF upload failed: ${error.message}`);
    } finally {
      setUploadingAmcAif(false);
    }
  };

  // Check for ongoing scraping and fetch stats on component mount
  useEffect(() => {
    fetchDataStats(); // Fetch initial data stats
    
    // Check browser status
    fetchBrowserStatus();
    
    // Poll browser status every 5 seconds if not ready
    const browserCheckInterval = setInterval(() => {
      if (!browserStatus.ready || browserStatus.checking) {
        fetchBrowserStatus();
      } else {
        clearInterval(browserCheckInterval);
      }
    }, 5000);
    
    const checkOngoingScraping = async () => {
      try {
        const timestamp = Date.now();
        const response = await fetch(`${BACKEND_URL}/api/scraping-progress?_t=${timestamp}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache'
          }
        });
        const data = await response.json();
        
        if (data.in_progress) {
          console.log('Detected ongoing scraping, starting progress polling...');
          setIsScrapingActive(true);
          setScrapingProgress(data);
          setUploadLoading(true);
          setUploadStatus({ 
            step: 'processing', 
            message: 'Scraping in progress (resumed monitoring)...' 
          });
          startProgressPolling();
        }
      } catch (err) {
        console.error('Error checking ongoing scraping:', err);
      }
    };
    
    checkOngoingScraping();
    fetchEmailConfig(); // Fetch email configuration
    fetchFilterConfig(); // Fetch filter configuration
    fetchZeroCriteriaConfig(); // Fetch zero criteria configuration
    fetchAvailableAmcs(); // Fetch available AMCs for AMC Details section
    
    // Cleanup on unmount
    return () => {
      clearInterval(browserCheckInterval);
      stopProgressPolling();
      stopHeartbeat();
    };
  }, []);

  // Auto-refresh every 60 seconds if enabled
  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchDataStats();
      }, 60000); // 60 seconds
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh]);

  // Fetch performance metrics when performance section is active
  useEffect(() => {
    if (activeSection === 'performance') {
      fetchPerformanceMetrics(50);
    }
  }, [activeSection]);

  const handleStopScraping = async () => {
    try {
      setStoppingScrap(true);
      const response = await fetch(`${BACKEND_URL}/api/stop-scraping`, {
        method: 'POST',
      });
      const data = await response.json();
      
      if (response.ok) {
        toast.success(data.message || 'Scraping will stop after current PM completes');
        
        // Update upload status to show stopping
        setUploadStatus({ 
          step: 'processing', 
          message: '⏹️ Stopping scraper... (will finish current PM)' 
        });
        
        // Start polling more aggressively to detect when scraping stops
        const checkStopComplete = setInterval(async () => {
          const progressResponse = await fetch(`${BACKEND_URL}/api/scraping-progress?_t=${Date.now()}`);
          const progressData = await progressResponse.json();
          
          if (!progressData.in_progress) {
            // Scraping has stopped
            clearInterval(checkStopComplete);
            setUploadStatus({ 
              step: 'complete', 
              message: '⏹️ Scraping stopped successfully' 
            });
            // Keep progress visible for 10 seconds after stopping
            setTimeout(() => {
              setScrapingProgress(null);
            }, 10000);
            setUploadLoading(false);
            stopProgressPolling();
            stopHeartbeat();
            
            // Clear message after 5 seconds
            setTimeout(() => {
              setUploadStatus(null);
            }, 5000);
          }
        }, 1000); // Check every second
        
        // Safety timeout - stop checking after 30 seconds
        setTimeout(() => {
          clearInterval(checkStopComplete);
        }, 30000);
      } else {
        toast.error(data.message || 'Failed to stop scraping');
      }
    } catch (err) {
      toast.error(`Error stopping scraping: ${err.message}`);
    } finally {
      setStoppingScrap(false);
    }
  };

  // Generate years (current year and past 3 years)
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => currentYear - i);

  const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
  ];

  const handleLogout = () => {
    localStorage.removeItem('isAdminLoggedIn');
    localStorage.removeItem('adminUser');
    toast.success('Logged out successfully');
    navigate('/');
  };

  const adminUser = localStorage.getItem('adminUser');

  const sidebarItems = [
    { id: 'scraper', label: 'Scraper Data', icon: Database },
    { id: 'performance', label: 'Performance Metrics', icon: BarChart3 },
    { id: 'logs', label: 'Backend Logs', icon: Activity },
    { id: 'browser-provider', label: 'Browser Provider', icon: Settings },
    { id: 'filter', label: 'Filter AMCs', icon: Filter },
    { id: 'zero-criteria', label: 'Zero Criteria Filter', icon: X },
    { id: 'negative-list', label: 'Negative List', icon: Ban },
    { id: 'email-recaptcha', label: 'Email & reCAPTCHA', icon: Settings },
    { id: 'amc', label: 'AMC Management', icon: Building2 },
    { id: 'amc-details', label: 'AMC Details', icon: Building2 },
    { id: 'admin-creds', label: 'Admin Credentials', icon: Key },
    { id: 'secrets', label: 'Secrets', icon: Lock },
    { id: 'scheduler', label: 'Scheduler', icon: Clock }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="flex">
        {/* Sidebar */}
        <div className="w-64 min-h-screen bg-white border-r border-slate-200 shadow-lg">
          {/* Header */}
          <div className="p-6 border-b border-slate-200">
            <div className="flex justify-center mb-3">
              <img src="/pmskart-logo.png" alt="PMS Kart Logo" className="h-16 w-auto" />
            </div>
            <div className="flex items-center gap-3 mb-2">
              <Database className="w-8 h-8 text-teal-600" />
              <h2 className="text-xl font-bold text-slate-900">Admin Panel</h2>
              <span
                className="ml-auto text-[10px] font-semibold px-2 py-0.5 rounded-full bg-teal-50 text-teal-700 border border-teal-200 tracking-wider"
                data-testid="admin-version-badge"
                title="Admin Panel version"
              >
                V3.7
              </span>
            </div>
            <p className="text-xs text-slate-500 mb-4">Portfolio Manager System</p>
            
            {/* User Info & Logout in Header */}
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <User className="w-4 h-4" />
                  <span className="truncate">{adminUser}</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleLogout}
                  className="flex items-center gap-2 border-red-300 text-red-600 hover:bg-red-50"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </Button>
              </div>
              
              {/* Emergency Stop Button - Always Visible */}
              <div className="mt-4 pt-4 border-t border-red-200">
                <EmergencyStopButton />
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="p-4">
            <ul className="space-y-2">
              {sidebarItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <li key={item.id}>
                    <button
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                        isActive
                          ? 'bg-teal-600 text-white shadow-md'
                          : 'text-slate-600 hover:bg-slate-100'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium text-sm">{item.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 p-8">
          {/* Section Header */}
          <div className="mb-8">
            <h1 className="text-4xl font-bold text-slate-900 mb-2 flex items-center gap-3">
              {sidebarItems.find(item => item.id === activeSection) && 
                React.createElement(sidebarItems.find(item => item.id === activeSection).icon, { className: "w-10 h-10 text-teal-600" })
              }
              {sidebarItems.find(item => item.id === activeSection)?.label}
            </h1>
            <p className="text-slate-600 text-lg">
              {activeSection === 'scraper' && 'Manually trigger data scraping from SEBI Portfolio Manager Monthly Reports'}
              {activeSection === 'performance' && 'View detailed timing breakdowns and performance metrics for all scraping operations'}
              {activeSection === 'logs' && 'Monitor backend logs in real-time: memory usage, cleanup operations, performance metrics, and system resources'}
              {activeSection === 'browser-provider' && 'Switch between BrowserCat and Browserless cloud browser services'}
              {activeSection === 'filter' && 'Configure minimum AUM threshold for filtering AMCs'}
              {activeSection === 'zero-criteria' && 'Hide schemes with zero performance for selected period'}
              {activeSection === 'negative-list' && 'Upload CSV to hide specific schemes from the landing page'}
              {activeSection === 'email-recaptcha' && 'Manage email configuration and reCAPTCHA settings'}
              {activeSection === 'amc' && 'Upload Master AMC CSV and manage AMC login data'}
              {activeSection === 'amc-details' && 'View and manage scheme data for any AMC'}
              {activeSection === 'admin-creds' && 'Update admin email and password credentials'}
              {activeSection === 'secrets' && 'Manage deployment secrets and API keys (BrowserCat, Browserless, Calendly, reCAPTCHA, etc.)'}
              {activeSection === 'scheduler' && 'Automate monthly scraping — runs on a chosen day for the previous month, emails a summary + Excel log'}
            </p>
          </div>

          {/* Scraper Data Section */}
          {activeSection === 'scraper' && (
            <div className="space-y-6">
              
              {/* Browser Status Warning Banner */}
              {!browserStatus.ready && (
                <Card className="border-amber-200 bg-amber-50">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      {browserStatus.checking ? (
                        <Loader2 className="w-5 h-5 text-amber-600 animate-spin mt-0.5" />
                      ) : (
                        <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <h3 className="font-semibold text-amber-900 mb-1">
                          {browserStatus.checking ? 'Installing Browser Components...' : 'Browser Not Ready'}
                        </h3>
                        <p className="text-sm text-amber-800">
                          {browserStatus.message}
                        </p>
                        {browserStatus.checking && (
                          <p className="text-xs text-amber-700 mt-2">
                            Please wait while the browser is being installed. This usually takes 1-2 minutes on first deployment.
                          </p>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              {/* Data Statistics & Refresh Controls */}
              <Card className="shadow-lg mb-6 bg-gradient-to-r from-blue-50 to-teal-50 border-blue-200">
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div>
                <CardTitle className="text-xl">Data Overview & Refresh Controls</CardTitle>
                <CardDescription>Monitor your database statistics and refresh data</CardDescription>
              </div>
              <div className="flex items-center gap-3">
                <Button
                  onClick={handleClearCacheAndRefresh}
                  disabled={refreshingData}
                  size="sm"
                  className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshingData ? 'animate-spin' : ''}`} />
                  Clear Cache & Refresh
                </Button>
                
                <Button
                  onClick={fetchDataStats}
                  disabled={refreshing}
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-2 border-teal-600 text-teal-600 hover:bg-teal-50"
                >
                  <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                  Refresh Stats
                </Button>
                
                <div className="flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-slate-200">
                  <Switch 
                    checked={autoRefresh}
                    onCheckedChange={setAutoRefresh}
                    id="auto-refresh-admin"
                  />
                  <label htmlFor="auto-refresh-admin" className="text-sm text-slate-600 cursor-pointer whitespace-nowrap">
                    Auto-refresh (60s)
                  </label>
                </div>
                
                {lastUpdated && (
                  <div className="text-xs text-slate-500 bg-white px-3 py-2 rounded-lg border border-slate-200">
                    Last updated: {lastUpdated.toLocaleTimeString()}
                  </div>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {dataStats ? (
              <div className="bg-white rounded-lg p-4 border border-blue-200">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-6">
                    <div>
                      <span className="text-3xl font-bold text-blue-900">{dataStats.totalRecords}</span>
                      <span className="text-blue-700 ml-2 text-lg">schemes displayed</span>
                    </div>
                    <div className="text-blue-600 text-lg">from</div>
                    <div>
                      <span className="text-3xl font-bold text-blue-900">{dataStats.totalPmsProcessed}</span>
                      <span className="text-blue-700 ml-2 text-lg">Portfolio Managers</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <div className="bg-slate-50 px-3 py-2 rounded-lg border border-slate-300">
                      <span className="font-semibold text-slate-700">{dataStats.totalSchemesFound}</span>
                      <span className="text-slate-600 ml-1">total schemes found</span>
                    </div>
                    <div className="bg-orange-50 px-3 py-2 rounded-lg border border-orange-300">
                      <span className="font-semibold text-orange-700">{dataStats.filteredOutAum}</span>
                      <span className="text-orange-600 ml-1">filtered (AUM &lt; 100 Cr)</span>
                    </div>
                    <div className="bg-orange-50 px-3 py-2 rounded-lg border border-orange-300">
                      <span className="font-semibold text-orange-700">{dataStats.filteredOut3Year}</span>
                      <span className="text-orange-600 ml-1">filtered (3Y = 0%)</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className="text-center text-slate-600 py-4">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Loading data statistics...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Year and Month Selectors */}
        <div className="mb-6">
          <Card className="bg-gradient-to-r from-teal-50 to-blue-50 border-teal-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-6 h-6" />
                Batch Scraping via CSV Upload
              </CardTitle>
              <CardDescription>
                Upload a CSV file with Portfolio Manager names to scrape data.
                {(year && month) ? ` Will use Year: ${year}, Month: ${month}` : " Will use current year and month-2."}
                <br />
                <span className="text-xs text-slate-500 mt-1 block">
                  CSV Format: First column should be named "Portfolio Manager", "pm_name", "PM Name", or "Name"
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label htmlFor="csvFile">CSV File (must have 'pm_name' or 'Portfolio Manager' column)</Label>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={handleDownloadSampleCSV}
                        className="text-teal-600 hover:text-teal-700"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download Sample CSV
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        onClick={handleDownloadScrapeLogs}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Download Scrape Logs
                      </Button>
                    </div>
                  </div>
                  <Input
                    id="csvFile"
                    type="file"
                    accept=".csv"
                    onChange={handleFileChange}
                    className="mt-2"
                  />
                  {csvFile && (
                    <p className="text-sm text-green-600 mt-2">✓ {csvFile.name} selected</p>
                  )}
                </div>
                
                {/* Force Re-scrape Checkbox */}
                <div className="flex items-center gap-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <input
                    type="checkbox"
                    id="forceRescrape"
                    checked={forceRescrape}
                    onChange={(e) => setForceRescrape(e.target.checked)}
                    className="w-4 h-4 text-teal-600 border-gray-300 rounded focus:ring-teal-500"
                  />
                  <label htmlFor="forceRescrape" className="text-sm text-amber-900 cursor-pointer">
                    <span className="font-semibold">Force Re-scrape:</span> Re-scrape data even if it already exists for this month
                  </label>
                </div>
                
                <button
                  onClick={handleUploadAndScrape}
                  disabled={uploadLoading || !csvFile || !browserStatus.ready}
                  className="w-full px-6 py-3 text-base font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {uploadLoading ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin inline" />
                      Uploading & Scraping...
                    </>
                  ) : (
                    <>
                      <Upload className="mr-2 h-5 w-5 inline" />
                      Upload CSV & Start Scraping
                    </>
                  )}
                </button>

                {/* Upload Status Indicator */}
                {uploadStatus && (
                  <Alert className={
                    uploadStatus.step === 'complete' 
                      ? "bg-green-50 border-green-200 animate-in fade-in-50 duration-300" 
                      : "bg-blue-50 border-blue-200 animate-in fade-in-50 duration-300"
                  }>
                    {uploadStatus.step === 'complete' ? (
                      <CheckCircle className="h-5 w-5 text-green-600" />
                    ) : (
                      <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />
                    )}
                    <AlertTitle className={
                      uploadStatus.step === 'complete' ? "text-green-900" : "text-blue-900"
                    }>
                      {uploadStatus.step === 'reading' && 'Step 1: Reading CSV File'}
                      {uploadStatus.step === 'uploading' && 'Step 2: Uploading to Server'}
                      {uploadStatus.step === 'processing' && 'Step 3: Scraping Data from SEBI'}
                      {uploadStatus.step === 'complete' && 'Complete!'}
                    </AlertTitle>
                    <AlertDescription className={
                      uploadStatus.step === 'complete' ? "text-green-800" : "text-blue-800"
                    }>
                      {uploadStatus.message}
                      {uploadStatus.step === 'processing' && (
                        <div className="mt-2 text-xs space-y-1">
                          <div className="font-semibold">⏱️ Please be patient - this process takes time:</div>
                          <div>• Each Portfolio Manager takes 15-30 seconds to scrape</div>
                          <div>• Your CSV will be processed sequentially</div>
                          <div>• Keep this tab open until completion</div>
                          <div className="mt-2 p-2 bg-yellow-50 border border-yellow-300 rounded text-yellow-800">
                            <span className="font-semibold">⚠️ Important:</span> Keep this browser tab active and visible to prevent the session from sleeping. Minimize interruptions for best results.
                          </div>
                          
                          {/* Real-time progress */}
                          {(isScrapingActive || scrapingProgress?.in_progress) && scrapingProgress && (
                            <div className="mt-3 p-3 bg-white rounded border border-blue-300">
                              <div className="flex items-center justify-between mb-2">
                                <div className="font-bold text-blue-900">
                                  Scraping Progress: {scrapingProgress.current_index || 0} / {scrapingProgress.total_pms || 0}
                                </div>
                                <div className="flex items-center gap-3">
                                  {/* Heartbeat Indicator */}
                                  <div className="flex items-center gap-2 bg-green-50 px-2 py-1 rounded border border-green-200">
                                    <div className={`w-2 h-2 rounded-full transition-all duration-300 ${
                                      heartbeatActive ? 'bg-green-500 shadow-lg shadow-green-400' : 'bg-green-400'
                                    }`}></div>
                                    <span className="text-xs text-green-700 font-medium">Heartbeat (15s)</span>
                                  </div>
                                  {/* Stop Button */}
                                  <Button
                                    onClick={handleStopScraping}
                                    disabled={stoppingScrap || scrapingProgress?.status === 'stopping'}
                                    size="sm"
                                    variant="destructive"
                                    className="flex items-center gap-1"
                                  >
                                    <XCircle className="w-4 h-4" />
                                    {scrapingProgress?.status === 'stopping' ? 'Stopping...' : 'Stop'}
                                  </Button>
                                </div>
                              </div>
                              {scrapingProgress?.current_pm && (
                                <div className="flex items-center gap-2">
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-600" />
                                  <span className="text-blue-800 font-medium">
                                    {scrapingProgress.status === 'skipping' ? 'Skipping' : 'Currently scraping'}: {scrapingProgress.current_pm}
                                  </span>
                                </div>
                              )}
                              {scrapingProgress?.status === 'skipping' && !scrapingProgress?.current_pm && (
                                <div className="flex items-center gap-2 text-yellow-700">
                                  <span className="text-sm font-medium">
                                    ⏭️ Processing skipped PMs (already have data)...
                                  </span>
                                </div>
                              )}
                              {scrapingProgress?.status === 'memory_cleanup' && (
                                <div className="mt-2 flex items-center gap-2 text-orange-700 bg-orange-50 p-2 rounded">
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                  <span className="text-sm font-medium">
                                    🧹 Memory cleanup in progress... (30s break)
                                  </span>
                                </div>
                              )}
                              {scrapingProgress?.completed && scrapingProgress.completed.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-3 text-xs">
                                  <span className="text-green-700">✓ Scraped: {scrapingProgress.successful || 0}</span>
                                  <span className="text-yellow-700">⏭️ Skipped: {scrapingProgress.skipped || 0}</span>
                                  {(scrapingProgress.failed || 0) > 0 && (
                                    <span className="text-red-700">✗ Failed: {scrapingProgress.failed}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="mt-2 bg-blue-100 p-2 rounded">
                            💡 Tip: Start with a small CSV (2-3 PMs) to test first!
                          </div>
                        </div>
                      )}
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Year and Month Selection for Batch Scraping */}
        <Card className="shadow-xl mb-6">
          <CardHeader>
            <CardTitle>Select Date for Scraping</CardTitle>
            <CardDescription>
              Choose year and month for the batch scraping operation (optional - defaults to current year, month-2)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="year">Year</Label>
                <Select value={year} onValueChange={setYear}>
                  <SelectTrigger id="year">
                    <SelectValue placeholder="Select Year (Optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {years.map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="month">Month</Label>
                <Select value={month} onValueChange={setMonth}>
                  <SelectTrigger id="month">
                    <SelectValue placeholder="Select Month (Optional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m) => (
                      <SelectItem key={m.value} value={m.value.toString()}>
                        {m.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Batch Results Display */}
        {batchResult && (
          <Card className="shadow-xl mt-6">
            <CardHeader>
              <CardTitle>Batch Scraping Results</CardTitle>
              <CardDescription>
                {batchResult.message}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 font-medium">Total Attempted</p>
                  <p className="text-3xl font-bold text-blue-900">{batchResult.total_attempted}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600 font-medium">Successful</p>
                  <p className="text-3xl font-bold text-green-900">{batchResult.successful}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg">
                  <p className="text-sm text-red-600 font-medium">Failed</p>
                  <p className="text-3xl font-bold text-red-900">{batchResult.failed}</p>
                </div>
              </div>

              {batchResult.results && batchResult.results.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-semibold text-lg mb-3">Detailed Results:</h3>
                  {batchResult.results.map((item, idx) => (
                    <Alert
                      key={idx}
                      className={
                        item.status === "success"
                          ? "bg-green-50 border-green-200"
                          : "bg-amber-50 border-amber-200"
                      }
                    >
                      {item.status === "success" ? (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-amber-600" />
                      )}
                      <AlertTitle
                        className={
                          item.status === "success" ? "text-green-900" : "text-amber-900"
                        }
                      >
                        {item.pm_name}
                      </AlertTitle>
                      <AlertDescription
                        className={
                          item.status === "success" ? "text-green-800" : "text-amber-800"
                        }
                      >
                        {item.message}
                        {item.tables_found > 0 && (
                          <span className="ml-2 font-medium">
                            ({item.tables_found} table{item.tables_found > 1 ? "s" : ""} found)
                          </span>
                        )}
                      </AlertDescription>
                    </Alert>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Performance Data Section */}
        <Card className="shadow-xl mt-6">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-purple-600" />
                <div>
                  <CardTitle>Performance Data Extractor</CardTitle>
                  <CardDescription>
                    Extract scheme performance data from all Portfolio Manager reports
                  </CardDescription>
                </div>
              </div>
            </div>
          </CardHeader>
          {performanceData && (
            <CardContent className="pt-6">
              {performanceData.status === "success" && performanceData.data.length > 0 ? (
                <div>
                  <Alert className="bg-green-50 border-green-200 mb-4">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <AlertTitle className="text-green-900">
                      Success!
                    </AlertTitle>
                    <AlertDescription className="text-green-800">
                      Found {performanceData.total_records} performance records from your database
                    </AlertDescription>
                  </Alert>
                  
                  <div className="overflow-x-auto max-h-96 overflow-y-auto border rounded-lg">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 sticky top-0">
                        <tr>
                          {performanceData.data[0] && Object.keys(performanceData.data[0]).map((key) => (
                            <th key={key} className="px-4 py-2 text-left font-medium text-slate-700 border-b">
                              {key}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {performanceData.data.slice(0, 50).map((row, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            {Object.values(row).map((value, vIdx) => (
                              <td key={vIdx} className="px-4 py-2 text-slate-600">
                                {value}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {performanceData.data.length > 50 && (
                      <div className="text-center py-3 bg-slate-50 text-sm text-slate-600">
                        Showing first 50 of {performanceData.data.length} records. Export CSV to see all.
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <Alert className="bg-amber-50 border-amber-200">
                  <AlertCircle className="h-5 w-5 text-amber-600" />
                  <AlertTitle className="text-amber-900">No Performance Data Found</AlertTitle>
                  <AlertDescription className="text-amber-800">
                    {performanceData.message || "No performance data available in the database. Try scraping some Portfolio Managers first."}
                  </AlertDescription>
                </Alert>
              )}
            </CardContent>
          )}
        </Card>
            </div>
          )}

          {/* Browser Provider Section */}
          {activeSection === 'browser-provider' && <BrowserProviderSection onProviderSwitched={fetchBrowserStatus} />}

          {/* Filter AMCs Section */}
          {activeSection === 'filter' && (
            <div className="space-y-6">
              {/* Filter Configuration Card */}
              <Card className="shadow-lg border-2 border-blue-200">
          <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b-2 border-blue-200">
            <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
              <Database className="w-6 h-6 text-blue-600" />
              Data Filter Configuration
            </CardTitle>
            <CardDescription>
              Set minimum AUM threshold to filter schemes displayed on landing page
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Current Filter Status */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">Current Filter</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-600">Minimum AUM Threshold:</span>
                  <span className="text-slate-800 font-bold text-lg">
                    {minAum === 0 ? 'No Filter (All schemes shown)' : `${minAum} Crores`}
                  </span>
                </div>
                {minAum > 0 && (
                  <div className="text-amber-600 text-xs flex items-center gap-1 mt-2">
                    <AlertCircle className="w-4 h-4" />
                    Schemes with AUM below {minAum} Cr are hidden from the main table
                  </div>
                )}
              </div>
            </div>

            {/* Update Filter Configuration */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800">Update AUM Filter</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Minimum AUM (in Crores)
                </label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={newMinAum}
                  onChange={(e) => setNewMinAum(e.target.value)}
                  placeholder="Enter minimum AUM (e.g., 100)"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-xs text-slate-500">
                  ℹ️ Enter 0 to show all schemes. Enter any value (e.g., 100) to hide schemes with AUM below that threshold.
                </p>
              </div>

              <button
                onClick={handleSaveFilterConfig}
                disabled={savingFilter || newMinAum === ''}
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {savingFilter ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Applying Filter...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Apply AUM Filter
                  </>
                )}
              </button>
            </div>

            {/* Info Section */}
            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2 flex items-center gap-2">
                <CheckCircle className="w-4 h-4" />
                How It Works
              </h4>
              <ul className="text-sm text-green-800 space-y-1 list-disc list-inside">
                <li>Set minimum AUM to filter schemes on the landing page</li>
                <li>Example: Set 100 to hide all schemes with AUM less than 100 Cr</li>
                <li>Set 0 to disable filtering and show all schemes</li>
                <li>Filter applies immediately after saving</li>
              </ul>
            </div>
          </CardContent>
        </Card>
            </div>
          )}

          {/* Zero Criteria Filter Section */}
          {activeSection === 'zero-criteria' && (
            <div className="space-y-6">
              <Card className="shadow-lg border-2 border-red-200">
                <CardHeader className="bg-gradient-to-r from-red-50 to-orange-50 border-b-2 border-red-200">
                  <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
                    <X className="w-6 h-6 text-red-600" />
                    Zero Criteria Filter
                  </CardTitle>
                  <CardDescription>
                    Hide schemes with zero performance for selected time period
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                  {/* Current Status */}
                  <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
                    <h3 className="font-semibold text-slate-800 mb-2">Current Status</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-slate-600">Filter Status:</span>
                        <span className={`font-bold text-lg ${zeroCriteriaEnabled ? 'text-green-600' : 'text-slate-600'}`}>
                          {zeroCriteriaEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                      {zeroCriteriaEnabled && zeroCriteriaPeriod && (
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-600">Active Period:</span>
                          <span className="text-slate-800 font-bold">
                            {zeroCriteriaPeriod === '2_year' ? '2 Year' : 
                             zeroCriteriaPeriod === '3_year' ? '3 Year' : '5 Year'}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Filter Options */}
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-800">Select Time Period</h3>
                    <p className="text-sm text-slate-600">
                      Choose one period to hide schemes where that period's performance = 0
                    </p>

                    <div className="grid grid-cols-3 gap-4">
                      {[
                        { value: '2_year', label: '2 Year' },
                        { value: '3_year', label: '3 Year' },
                        { value: '5_year', label: '5 Year' }
                      ].map((period) => (
                        <button
                          key={period.value}
                          onClick={() => {
                            const isCurrentlySelected = zeroCriteriaEnabled && zeroCriteriaPeriod === period.value;
                            if (isCurrentlySelected) {
                              // Disable if clicking the same period
                              handleUpdateZeroCriteria(false, null);
                            } else {
                              // Enable with new period
                              handleUpdateZeroCriteria(true, period.value);
                            }
                          }}
                          className={`p-4 border-2 rounded-lg font-semibold transition-all ${
                            zeroCriteriaEnabled && zeroCriteriaPeriod === period.value
                              ? 'bg-red-600 text-white border-red-600 shadow-lg'
                              : 'bg-white text-slate-700 border-slate-300 hover:border-red-300 hover:bg-red-50'
                          }`}
                        >
                          {period.label}
                          {zeroCriteriaEnabled && zeroCriteriaPeriod === period.value && (
                            <div className="text-xs mt-1">Active</div>
                          )}
                        </button>
                      ))}
                    </div>

                    {zeroCriteriaEnabled && (
                      <button
                        onClick={() => handleUpdateZeroCriteria(false, null)}
                        className="w-full mt-4 px-4 py-2 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
                      >
                        Disable Filter
                      </button>
                    )}
                  </div>

                  {/* Info Section */}
                  <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />
                      How It Works
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                      <li>Select one time period (2yr, 3yr, or 5yr)</li>
                      <li>Schemes with 0% performance for that period will be hidden</li>
                      <li>Only ONE period can be active at a time</li>
                      <li>Click the active period again to disable the filter</li>
                      <li>Filter applies immediately on the landing page</li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Negative List Section */}
          {activeSection === 'negative-list' && <NegativeListSection />}

          {/* Email & reCAPTCHA Section */}
          {activeSection === 'email-recaptcha' && (
            <div className="space-y-6">
              {/* Email Configuration Card */}
              <Card className="shadow-lg border-2 border-purple-200">
          <CardHeader className="bg-gradient-to-r from-purple-50 to-pink-50 border-b-2 border-purple-200">
            <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
              <Mail className="w-6 h-6 text-purple-600" />
              Email Configuration
            </CardTitle>
            <CardDescription>
              Configure sender email for contact form submissions
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6 space-y-6">
            {/* Current Configuration Status */}
            <div className="bg-slate-50 p-4 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">Current Status</h3>
              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-slate-600">Configured:</span>
                  <span className={`font-semibold ${emailConfig.configured ? 'text-green-600' : 'text-red-600'}`}>
                    {emailConfig.configured ? '✓ Yes' : '✗ No'}
                  </span>
                </div>
                {emailConfig.configured && (
                  <>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-600">Sender Email:</span>
                      <span className="text-slate-800">{emailConfig.sender_email}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-slate-600">Password:</span>
                      <span className="text-green-600">{emailConfig.has_password ? '✓ Configured (Encrypted)' : '✗ Not Set'}</span>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Update Email Configuration */}
            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800">Update Configuration</h3>
              
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Gmail Business Email
                </label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="sender@yourcompany.com"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>

              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">
                  Gmail App Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter Gmail App Password (not regular password)"
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-slate-500">
                  ⚠️ Use Gmail App Password, not your regular Gmail password. Password will be encrypted before storage.
                </p>
              </div>

              <button
                onClick={handleSaveEmailConfig}
                disabled={savingEmail || !newEmail || !newPassword}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {savingEmail ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Configuration
                  </>
                )}
              </button>
            </div>

            {/* Test Email */}
            {emailConfig.configured && (
              <div className="space-y-4 pt-4 border-t border-slate-200">
                <h3 className="font-semibold text-slate-800">Test Email</h3>
                
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-slate-700">
                    Test Recipient Email
                  </label>
                  <input
                    type="email"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    placeholder="test@example.com"
                    className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <button
                  onClick={handleTestEmail}
                  disabled={testingEmail || !testEmail}
                  className="w-full bg-green-600 hover:bg-green-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  {testingEmail ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      Send Test Email
                    </>
                  )}
                </button>
              </div>
            )}

            {/* Setup Instructions */}
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                Gmail App Password Setup
              </h4>
              <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                <li>Go to your Google Account settings</li>
                <li>Enable 2-Factor Authentication (if not already enabled)</li>
                <li>Go to Security → App Passwords</li>
                <li>Generate a new App Password for "Mail"</li>
                <li>Copy the 16-character password and paste it above</li>
              </ol>
            </div>
          </CardContent>
        </Card>
            </div>
          )}

          {/* AMC Management Section */}
          {activeSection === 'amc' && (
            <div className="space-y-6">
              {/* AMC Master CSV Upload Card */}
              <Card className="overflow-hidden">
          <CardHeader className="bg-gradient-to-r from-teal-600 to-cyan-600">
            <CardTitle className="text-white flex items-center gap-2">
              <Upload className="w-5 h-5" />
              AMC Master CSV Upload
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="space-y-4">
              {/* Description */}
              <div className="bg-slate-50 p-4 rounded-lg">
                <p className="text-sm text-slate-700">
                  Upload a Master CSV file containing AMC names and their login email addresses (up to 5 emails per AMC).
                </p>
              </div>

              {/* Download Buttons */}
              <div className="grid grid-cols-2 gap-4">
                <button
                  onClick={handleDownloadSampleCsv}
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Sample CSV
                </button>
                <button
                  onClick={handleDownloadCurrentAmcData}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
                >
                  <Download className="w-4 h-4" />
                  Download Current AMC Data
                </button>
              </div>

              {/* File Upload */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Select CSV File
                </label>
                <input
                  id="amc-csv-input"
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileSelect}
                  className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                {amcCsvFile && (
                  <p className="text-sm text-green-600 mt-2">
                    ✓ Selected: {amcCsvFile.name}
                  </p>
                )}
              </div>

              {/* Upload Button */}
              <button
                onClick={handleUploadAmcCsv}
                disabled={uploadingCsv || !amcCsvFile}
                className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-slate-300 disabled:cursor-not-allowed text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
              >
                {uploadingCsv ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload CSV
                  </>
                )}
              </button>

              {/* Upload Result */}
              {csvUploadResult && (
                <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
                  <h4 className="font-semibold text-green-900 mb-2">Upload Successful!</h4>
                  <div className="text-sm text-green-800 space-y-1">
                    <p>✓ {csvUploadResult.uploaded} new AMCs added</p>
                    <p>✓ {csvUploadResult.updated} existing AMCs updated</p>
                    {csvUploadResult.errors && csvUploadResult.errors.length > 0 && (
                      <div className="mt-2">
                        <p className="font-semibold text-red-600">Errors:</p>
                        <ul className="list-disc list-inside text-red-600 max-h-32 overflow-y-auto">
                          {csvUploadResult.errors.map((error, idx) => (
                            <li key={idx}>{error}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* CSV Format Info */}
              <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  CSV Format
                </h4>
                <div className="text-sm text-blue-800 space-y-1">
                  <p><strong>Columns:</strong> AMC Name, Email 1, Email 2, Email 3, Email 4, Email 5</p>
                  <p><strong>Note:</strong> First row should be headers. Each AMC can have 1-5 login emails.</p>
                  <p><strong>Behavior:</strong> Uploading will overwrite existing AMC data (passwords are preserved).</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
            </div>
          )}

          {/* AMC Details Section */}
          {activeSection === 'amc-details' && (
            <div className="space-y-6">
              {/* AMC Selector */}
              <Card className="shadow-lg border-2 border-teal-200">
                <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b-2 border-teal-200">
                  <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
                    <Building2 className="w-6 h-6 text-teal-600" />
                    Select AMC
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                        Choose an AMC to view and manage their schemes
                      </Label>
                      <select
                        value={selectedAmcForDetails}
                        onChange={(e) => handleAmcDetailsSelection(e.target.value)}
                        className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 text-sm"
                      >
                        <option value="">-- Select an AMC --</option>
                        {availableAmcs.map((amc, idx) => (
                          <option key={idx} value={amc}>
                            {amc}
                          </option>
                        ))}
                      </select>
                    </div>

                    {selectedAmcForDetails && (
                      <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
                        <p className="text-sm text-teal-800">
                          <strong>Selected:</strong> {selectedAmcForDetails}
                        </p>
                        <p className="text-xs text-teal-600 mt-1">
                          You can now upload CSV and view schemes for this AMC
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Upload PMS Scheme Data */}
              {selectedAmcForDetails && (
                <Card className="shadow-lg border-2 border-blue-200">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50 border-b-2 border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
                          <Upload className="w-6 h-6 text-blue-600" />
                          Upload PMS Scheme Data
                        </CardTitle>
                        <p className="text-xs text-slate-600 mt-1">For Portfolio Management Schemes (PMS)</p>
                      </div>
                      <Button
                        onClick={handleDownloadAmcSampleCsv}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 border-blue-400 text-blue-600 hover:bg-blue-50"
                      >
                        <Download className="w-4 h-4" />
                        Download Sample
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                          Select PMS CSV File
                        </Label>
                        <input
                          id="amc-details-csv-input"
                          type="file"
                          accept=".csv"
                          onChange={handleAmcDetailsCsvSelect}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                          disabled={uploadingAmcDetails}
                        />
                        {amcDetailsCsvFile && (
                          <p className="text-sm text-green-600 mt-2">
                            ✓ Selected: {amcDetailsCsvFile.name}
                          </p>
                        )}
                      </div>

                      <Button
                        onClick={handleAmcDetailsUploadCsv}
                        disabled={uploadingAmcDetails || !amcDetailsCsvFile}
                        className="w-full flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-300"
                      >
                        {uploadingAmcDetails ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading PMS Data...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload PMS CSV
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Upload AIF Data */}
              {selectedAmcForDetails && (
                <Card className="shadow-lg border-2 border-indigo-200">
                  <CardHeader className="bg-gradient-to-r from-indigo-50 to-purple-50 border-b-2 border-indigo-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
                          <Upload className="w-6 h-6 text-indigo-600" />
                          Upload AIF Data
                          <span style={{
                            border: '1.5px solid #6366f1',
                            borderRadius: '6px',
                            padding: '2px 6px',
                            fontSize: '9px',
                            fontWeight: '800',
                            color: '#6366f1',
                            letterSpacing: '0.5px',
                            marginLeft: '8px'
                          }}>
                            AIF
                          </span>
                        </CardTitle>
                        <p className="text-xs text-slate-600 mt-1">For Alternative Investment Funds (AIF)</p>
                      </div>
                      <Button
                        onClick={handleDownloadAifSampleCsv}
                        variant="outline"
                        size="sm"
                        className="flex items-center gap-2 border-indigo-400 text-indigo-600 hover:bg-indigo-50"
                      >
                        <Download className="w-4 h-4" />
                        Download Sample
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    <div className="space-y-4">
                      <div>
                        <Label className="text-sm font-semibold text-slate-700 mb-2 block">
                          Select AIF CSV File
                        </Label>
                        <input
                          id="amc-aif-csv-input"
                          type="file"
                          accept=".csv"
                          onChange={handleAmcAifCsvSelect}
                          className="w-full px-3 py-2 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          disabled={uploadingAmcAif}
                        />
                        {amcAifCsvFile && (
                          <p className="text-sm text-green-600 mt-2">
                            ✓ Selected: {amcAifCsvFile.name}
                          </p>
                        )}
                      </div>

                      <Button
                        onClick={handleAmcAifUploadCsv}
                        disabled={uploadingAmcAif || !amcAifCsvFile}
                        className="w-full flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-300"
                      >
                        {uploadingAmcAif ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Uploading AIF Data...
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            Upload AIF CSV
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* View Schemes */}
              {selectedAmcForDetails && (
                <Card className="shadow-lg border-2 border-green-200">
                  <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b-2 border-green-200">
                    <CardTitle className="flex items-center gap-2 text-xl text-slate-800">
                      <FileText className="w-6 h-6 text-green-600" />
                      Scheme Data
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-6">
                    {loadingAmcDetails ? (
                      <div className="flex items-center justify-center py-12">
                        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                        <span className="ml-3 text-slate-600">Loading schemes...</span>
                      </div>
                    ) : amcDetailsSchemes.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                        <p className="text-slate-600 text-lg font-medium mb-2">
                          No schemes found
                        </p>
                        <p className="text-slate-500 text-sm">
                          Upload a CSV file to add scheme data for {selectedAmcForDetails}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between flex-wrap gap-3">
                          <div className="flex items-center gap-4">
                            <p className="text-sm text-slate-600">
                              <strong>{amcDetailsSchemes.filter(s => 
                                amcSchemeFilter === 'all' ? true :
                                amcSchemeFilter === 'aif' ? s.scheme_category === 'AIF' :
                                s.scheme_category !== 'AIF'
                              ).length}</strong> scheme(s) found
                            </p>
                            
                            {/* Filter Tabs */}
                            <div className="flex items-center gap-2 bg-slate-100 p-1 rounded-lg">
                              <button
                                onClick={() => setAmcSchemeFilter('all')}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  amcSchemeFilter === 'all' 
                                    ? 'bg-white text-slate-900 shadow-sm' 
                                    : 'text-slate-600 hover:text-slate-900'
                                }`}
                              >
                                All ({amcDetailsSchemes.length})
                              </button>
                              <button
                                onClick={() => setAmcSchemeFilter('pms')}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  amcSchemeFilter === 'pms' 
                                    ? 'bg-white text-slate-900 shadow-sm' 
                                    : 'text-slate-600 hover:text-slate-900'
                                }`}
                              >
                                PMS ({amcDetailsSchemes.filter(s => s.scheme_category !== 'AIF').length})
                              </button>
                              <button
                                onClick={() => setAmcSchemeFilter('aif')}
                                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                                  amcSchemeFilter === 'aif' 
                                    ? 'bg-white text-indigo-600 shadow-sm' 
                                    : 'text-slate-600 hover:text-slate-900'
                                }`}
                              >
                                AIF ({amcDetailsSchemes.filter(s => s.scheme_category === 'AIF').length})
                              </button>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-3">
                            <Button
                              onClick={handleDownloadAmcSampleCsv}
                              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                            >
                              <Download className="w-4 h-4" />
                              Download Sample CSV
                            </Button>
                            <Button
                              onClick={() => setShowAmcSchemesModal(true)}
                              className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                            >
                              <Eye className="w-4 h-4" />
                              View All Schemes
                            </Button>
                          </div>
                        </div>

                        {/* Scheme List Preview */}
                        <div className="border border-slate-200 rounded-lg overflow-hidden">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                <th className="px-4 py-3 text-left font-semibold text-slate-700">Type</th>
                                <th className="px-4 py-3 text-left font-semibold text-slate-700">Scheme Name</th>
                                <th className="px-4 py-3 text-center font-semibold text-slate-700">1M %</th>
                                <th className="px-4 py-3 text-center font-semibold text-slate-700">3M %</th>
                                <th className="px-4 py-3 text-center font-semibold text-slate-700">6M %</th>
                                <th className="px-4 py-3 text-center font-semibold text-slate-700">1Y %</th>
                                <th className="px-4 py-3 text-left font-semibold text-slate-700">AUM (Cr)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {amcDetailsSchemes
                                .filter(s => 
                                  amcSchemeFilter === 'all' ? true :
                                  amcSchemeFilter === 'aif' ? s.scheme_category === 'AIF' :
                                  s.scheme_category !== 'AIF'
                                )
                                .slice(0, 5)
                                .map((scheme, idx) => (
                                <tr 
                                  key={idx} 
                                  className="border-t border-slate-100 hover:bg-slate-50"
                                  style={scheme.scheme_category === 'AIF' ? { borderLeft: '4px solid #6366f1' } : {}}
                                >
                                  <td className="px-4 py-3">
                                    {scheme.scheme_category === 'AIF' ? (
                                      <div style={{
                                        border: '1.5px solid #6366f1',
                                        borderRadius: '6px',
                                        padding: '2px 6px',
                                        fontSize: '9px',
                                        fontWeight: '800',
                                        color: '#6366f1',
                                        letterSpacing: '0.5px',
                                        display: 'inline-block'
                                      }}>
                                        AIF
                                      </div>
                                    ) : (
                                      <span className="text-xs text-slate-500 font-medium">PMS</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-slate-800 font-medium">{scheme.scheme_name || '-'}</td>
                                  <td className={`px-4 py-3 text-center font-semibold ${parseFloat(scheme['1_month']) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {scheme['1_month'] || '-'}
                                  </td>
                                  <td className={`px-4 py-3 text-center font-semibold ${parseFloat(scheme['3_month']) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {scheme['3_month'] || '-'}
                                  </td>
                                  <td className={`px-4 py-3 text-center font-semibold ${parseFloat(scheme['6_month']) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {scheme['6_month'] || '-'}
                                  </td>
                                  <td className={`px-4 py-3 text-center font-semibold ${parseFloat(scheme['1_year']) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                                    {scheme['1_year'] || '-'}
                                  </td>
                                  <td className="px-4 py-3 text-slate-600">
                                    {scheme.aum && scheme.aum !== '0' ? scheme.aum : 'N/A'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                          {amcDetailsSchemes.filter(s => 
                            amcSchemeFilter === 'all' ? true :
                            amcSchemeFilter === 'aif' ? s.scheme_category === 'AIF' :
                            s.scheme_category !== 'AIF'
                          ).length > 5 && (
                            <div className="bg-slate-50 px-4 py-2 text-center text-sm text-slate-600">
                              + {amcDetailsSchemes.filter(s => 
                                amcSchemeFilter === 'all' ? true :
                                amcSchemeFilter === 'aif' ? s.scheme_category === 'AIF' :
                                s.scheme_category !== 'AIF'
                              ).length - 5} more schemes
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Performance Metrics Section */}
          {activeSection === 'performance' && (
            <div className="space-y-6">
              {/* Header with Refresh Button */}
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">Scraping Performance Insights</h2>
                  <p className="text-slate-600 mt-1">Detailed timing breakdown for every scrape operation</p>
                </div>
                <Button
                  onClick={() => fetchPerformanceMetrics(50)}
                  disabled={loadingMetrics}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {loadingMetrics ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </>
                  )}
                </Button>
              </div>

              {loadingMetrics ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <Loader2 className="w-12 h-12 animate-spin mx-auto text-teal-600 mb-4" />
                    <p className="text-slate-600">Loading performance metrics...</p>
                  </CardContent>
                </Card>
              ) : performanceMetrics && performanceMetrics.total_records > 0 ? (
                <>
                  {/* Statistics Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
                      <CardContent className="p-6">
                        <div className="text-sm text-blue-600 mb-1 font-medium">Average Time</div>
                        <div className="text-3xl font-bold text-blue-900">
                          {performanceMetrics.statistics.average_total_time}s
                        </div>
                        <div className="text-xs text-blue-600 mt-1">Per scrape operation</div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
                      <CardContent className="p-6">
                        <div className="text-sm text-green-600 mb-1 font-medium">Fastest Scrape</div>
                        <div className="text-3xl font-bold text-green-900">
                          {performanceMetrics.statistics.min_total_time}s
                        </div>
                        <div className="text-xs text-green-600 mt-1">Best performance</div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200">
                      <CardContent className="p-6">
                        <div className="text-sm text-orange-600 mb-1 font-medium">Slowest Scrape</div>
                        <div className="text-3xl font-bold text-orange-900">
                          {performanceMetrics.statistics.max_total_time}s
                        </div>
                        <div className="text-xs text-orange-600 mt-1">Needs attention</div>
                      </CardContent>
                    </Card>

                    <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
                      <CardContent className="p-6">
                        <div className="text-sm text-purple-600 mb-1 font-medium">Total Scrapes</div>
                        <div className="text-3xl font-bold text-purple-900">
                          {performanceMetrics.statistics.total_scrapes}
                        </div>
                        <div className="text-xs text-purple-600 mt-1">
                          ✅ {performanceMetrics.statistics.successful_scrapes} successful
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  {/* Average Step Timing Chart */}
                  {performanceMetrics.statistics.step_averages && (
                    <Card>
                      <CardHeader>
                        <CardTitle>Average Time by Step</CardTitle>
                        <CardDescription>Where does the time go? (in seconds)</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={[
                            { name: 'Page Nav', value: performanceMetrics.statistics.step_averages.page_navigation },
                            { name: 'Form Load', value: performanceMetrics.statistics.step_averages.form_load_wait },
                            { name: 'PM Select', value: performanceMetrics.statistics.step_averages.pm_selection },
                            { name: 'Results Wait', value: performanceMetrics.statistics.step_averages.results_wait },
                            { name: 'Content Fetch', value: performanceMetrics.statistics.step_averages.content_fetch },
                            { name: 'HTML Parse', value: performanceMetrics.statistics.step_averages.html_parsing },
                            { name: 'Extract Tables', value: performanceMetrics.statistics.step_averages.table_extraction },
                          ]}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                            <YAxis label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft' }} />
                            <Tooltip formatter={(value) => `${value}s`} />
                            <Bar dataKey="value" fill="#0d9488" />
                          </BarChart>
                        </ResponsiveContainer>
                        <div className="mt-4 p-4 bg-teal-50 rounded-lg">
                          <p className="text-sm text-teal-900 font-medium">💡 Key Insight</p>
                          <p className="text-sm text-teal-700 mt-1">
                            The "Results Wait" step is typically the longest because it waits for SEBI's server to respond and render the tables.
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Recent Scrapes Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle>Recent Scrapes ({performanceMetrics.total_records})</CardTitle>
                      <CardDescription>Click on any row to see detailed timing breakdown</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-slate-200">
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">PM Name</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Date/Time</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Status</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Tables</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Total Time</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Page Nav</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 bg-blue-50">Network</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 bg-blue-50">Form Render</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Results Wait</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 bg-blue-50">Page Load</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 bg-blue-50">Validation</th>
                              <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700">Details</th>
                            </tr>
                          </thead>
                          <tbody>
                            {performanceMetrics.logs.map((log, index) => {
                              const timing = log.timing_metrics || {};
                              const isExpanded = expandedPM === index;
                              
                              // Find the slowest step
                              const steps = [
                                { name: 'Page Navigation', value: timing.page_navigation || 0 },
                                { name: 'Form Load Wait', value: timing.form_load_wait || 0 },
                                { name: 'PM Selection', value: timing.pm_selection || 0 },
                                { name: 'Results Wait', value: timing.results_wait || 0 },
                                { name: 'Content Fetch', value: timing.content_fetch || 0 },
                                { name: 'Table Extraction', value: timing.table_extraction || 0 }
                              ];
                              const bottleneck = steps.reduce((max, step) => step.value > max.value ? step : max, steps[0]);
                              
                              // Determine row color based on total time
                              const totalTime = timing.total_time || 0;
                              const rowClass = totalTime > 60 ? 'bg-red-50' : totalTime > 40 ? 'bg-orange-50' : totalTime > 25 ? 'bg-yellow-50' : 'bg-green-50';
                              
                              return (
                                <React.Fragment key={index}>
                                  <tr className={`border-b border-slate-200 hover:bg-slate-100 cursor-pointer ${rowClass}`} onClick={() => setExpandedPM(isExpanded ? null : index)}>
                                    <td className="px-4 py-3 text-sm text-slate-900 font-medium">
                                      {log.pm_name}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">
                                      {log.date} {log.time}
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      {(log.status === 'successful' || log.status === 'success') ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-green-100 text-green-700 text-xs font-medium">
                                          <CheckCircle className="w-3 h-3" />
                                          Success
                                        </span>
                                      ) : log.status === 'no_data' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-blue-100 text-blue-700 text-xs font-medium">
                                          No Data
                                        </span>
                                      ) : log.status === 'skipped' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-yellow-100 text-yellow-700 text-xs font-medium">
                                          Skipped
                                        </span>
                                      ) : log.status === 'error' ? (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                          <XCircle className="w-3 h-3" />
                                          Error
                                        </span>
                                      ) : (
                                        <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 text-xs font-medium">
                                          <XCircle className="w-3 h-3" />
                                          Failed
                                        </span>
                                      )}
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">
                                      {log.tables_extracted}
                                    </td>
                                    <td className="px-4 py-3 text-sm font-semibold">
                                      <span className={`${totalTime > 60 ? 'text-red-700' : totalTime > 40 ? 'text-orange-700' : totalTime > 25 ? 'text-yellow-700' : 'text-green-700'}`}>
                                        {totalTime}s
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">
                                      {(timing.page_navigation || 0).toFixed(2)}s
                                    </td>
                                    <td className="px-4 py-3 text-sm text-blue-700 font-medium bg-blue-50">
                                      {(timing.network_request || 0).toFixed(2)}s
                                    </td>
                                    <td className="px-4 py-3 text-sm text-blue-700 font-medium bg-blue-50">
                                      {(timing.form_render || 0).toFixed(2)}s
                                    </td>
                                    <td className="px-4 py-3 text-sm text-slate-600">
                                      {(timing.results_wait || 0).toFixed(2)}s
                                    </td>
                                    <td className="px-4 py-3 text-sm text-blue-700 font-medium bg-blue-50">
                                      {(timing.results_page_load || 0).toFixed(2)}s
                                    </td>
                                    <td className="px-4 py-3 text-sm text-blue-700 font-medium bg-blue-50">
                                      {(timing.results_validation || 0).toFixed(3)}s
                                    </td>
                                    <td className="px-4 py-3 text-sm">
                                      <Button variant="ghost" size="sm">
                                        {isExpanded ? (
                                          <>
                                            <X className="w-4 h-4 mr-1" />
                                            Hide
                                          </>
                                        ) : (
                                          <>
                                            <Eye className="w-4 h-4 mr-1" />
                                            View
                                          </>
                                        )}
                                      </Button>
                                    </td>
                                  </tr>
                                  
                                  {/* Expanded Details */}
                                  {isExpanded && (
                                    <tr className="bg-slate-50">
                                      <td colSpan="7" className="px-4 py-4">
                                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Browser Init</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.browser_init || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Page Navigation</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.page_navigation || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Form Load Wait</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.form_load_wait || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Popup Handling</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.popup_handling || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">PM Selection</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.pm_selection || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Form Filling</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.form_filling || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Button Click</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.button_click || 0}s</div>
                                          </div>
                                          <div className={`p-3 rounded border-2 ${bottleneck.name === 'Results Wait' ? 'bg-orange-50 border-orange-300' : 'bg-white border-slate-200'}`}>
                                            <div className="text-xs text-slate-500 mb-1">Results Wait ⏱️</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.results_wait || 0}s</div>
                                            {bottleneck.name === 'Results Wait' && (
                                              <div className="text-xs text-orange-600 mt-1">Bottleneck!</div>
                                            )}
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Content Fetch</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.content_fetch || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">HTML Parsing</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.html_parsing || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Table Extraction</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.table_extraction || 0}s</div>
                                          </div>
                                          <div className="p-3 bg-white rounded border border-slate-200">
                                            <div className="text-xs text-slate-500 mb-1">Contact Extraction</div>
                                            <div className="text-lg font-semibold text-slate-900">{timing.contact_extraction || 0}s</div>
                                          </div>
                                        </div>
                                        {timing.retry_attempt > 0 && (
                                          <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded">
                                            <p className="text-sm text-yellow-800">
                                              ⚠️ This scrape required a retry attempt ({timing.retry_attempt}s)
                                            </p>
                                          </div>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Insights Card */}
                  <Card className="bg-gradient-to-br from-teal-50 to-teal-100 border-teal-200">
                    <CardHeader>
                      <CardTitle className="text-teal-900">🎯 Performance Insights</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">✓</div>
                          <div>
                            <p className="font-medium text-slate-900">Fast scrapes (&lt;30s)</p>
                            <p className="text-sm text-slate-600">Excellent! SEBI server is responding quickly.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded-full bg-yellow-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">⚠</div>
                          <div>
                            <p className="font-medium text-slate-900">Medium scrapes (30-60s)</p>
                            <p className="text-sm text-slate-600">Normal range. Server may be moderately loaded.</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">!</div>
                          <div>
                            <p className="font-medium text-slate-900">Slow scrapes (&gt;60s)</p>
                            <p className="text-sm text-slate-600">Investigate! Check "Results Wait" time - likely SEBI server latency or network issues.</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </>
              ) : (
                <Card>
                  <CardContent className="p-12 text-center">
                    <BarChart3 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                    <h3 className="text-xl font-semibold text-slate-700 mb-2">No Performance Data Yet</h3>
                    <p className="text-slate-600 mb-6">
                      Run a scrape to see detailed timing metrics and performance insights here.
                    </p>
                    <Button
                      onClick={() => setActiveSection('scraper')}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      Go to Scraper
                    </Button>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Backend Logs Section */}
          {activeSection === 'logs' && (
            <BackendLogsMonitor />
          )}

          {/* Admin Credentials Section */}
          {activeSection === 'admin-creds' && <AdminCredentialsSection />}

          {/* Secrets Management Section */}
          {activeSection === 'secrets' && <SecretsSection />}

          {/* Scheduler Section */}
          {activeSection === 'scheduler' && <SchedulerSection />}

        </div>
      </div>

      {/* AMC Schemes Modal */}
      {showAmcSchemesModal && amcDetailsSchemes.length > 0 && (() => {
        // Prepare data for grouped bar chart
        const periods = ['1M', '3M', '6M', '1Y', '2Y', '3Y', '5Y', 'SI'];
        const periodKeys = ['1_month', '3_month', '6_month', '1_year', '2_year', '3_year', '5_year', 'since_inception'];
        const chartData = periods.map((period, idx) => {
          const dataPoint = { period };
          amcDetailsSchemes.forEach(scheme => {
            dataPoint[scheme.scheme_name] = parseFloat(scheme[periodKeys[idx]]) || 0;
          });
          return dataPoint;
        });
        
        // Colors for different schemes
        const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#FFA07A', '#98D8C8', '#F7B731', '#5F27CD', '#00D2D3'];
        
        return (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-y-auto"
          onClick={(e) => {
            if (e.target === e.currentTarget) setShowAmcSchemesModal(false);
          }}
        >
          <div className="bg-white rounded-lg shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-4 flex items-center justify-between z-10">
              <div>
                <h2 className="text-2xl font-bold">{selectedAmcForDetails} - Schemes</h2>
                <p className="text-purple-100 text-sm mt-1">Viewing {amcDetailsSchemes.length} scheme(s)</p>
              </div>
              <button 
                onClick={() => setShowAmcSchemesModal(false)}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="p-6 space-y-6">
              {/* Performance Comparison Bar Chart */}
              <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-lg p-6 border border-blue-200">
                <h3 className="text-xl font-semibold text-slate-800 mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-teal-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  Performance Comparison
                </h3>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis 
                      dataKey="period" 
                      tick={{ fill: '#475569', fontSize: 12 }}
                    />
                    <YAxis 
                      tick={{ fill: '#475569', fontSize: 12 }}
                      label={{ value: 'Returns (%)', angle: -90, position: 'insideLeft', style: { fill: '#475569' } }}
                    />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: 'white', 
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                      }}
                      formatter={(value) => [`${value.toFixed(2)}%`, '']}
                    />
                    <Legend 
                      wrapperStyle={{ paddingTop: '20px' }}
                      iconType="circle"
                    />
                    {amcDetailsSchemes.map((scheme, idx) => (
                      <Bar 
                        key={scheme.scheme_name}
                        dataKey={scheme.scheme_name} 
                        fill={colors[idx % colors.length]}
                        radius={[4, 4, 0, 0]}
                        name={scheme.scheme_name.length > 30 ? scheme.scheme_name.substring(0, 30) + '...' : scheme.scheme_name}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Performance Metrics Table */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-slate-100 to-slate-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">Performance Metrics (%)</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 sticky left-0 bg-slate-50">Scheme</th>
                        {periods.map((period) => (
                          <th key={period} className="px-4 py-3 text-center text-sm font-semibold text-slate-700 whitespace-nowrap">
                            {period}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {amcDetailsSchemes.map((scheme, idx) => (
                        <tr 
                          key={scheme.scheme_name} 
                          className="border-b border-slate-100 hover:bg-slate-50"
                          style={scheme.scheme_category === 'AIF' ? { borderLeft: '4px solid #6366f1' } : {}}
                        >
                          <td className="px-4 py-3 text-sm sticky left-0 bg-white max-w-xs">
                            <div className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="font-semibold text-slate-900">{selectedAmcForDetails}</div>
                                <div className="text-xs text-slate-600 mt-0.5">{scheme.scheme_name}</div>
                              </div>
                              {scheme.scheme_category === 'AIF' && (
                                <div style={{
                                  border: '1.5px solid #6366f1',
                                  borderRadius: '6px',
                                  padding: '2px 6px',
                                  fontSize: '9px',
                                  fontWeight: '800',
                                  color: '#6366f1',
                                  letterSpacing: '0.5px',
                                  flexShrink: 0
                                }}>
                                  AIF
                                </div>
                              )}
                            </div>
                          </td>
                          {periodKeys.map((key) => (
                            <td 
                              key={key}
                              className={`px-4 py-3 text-center text-sm font-semibold whitespace-nowrap ${
                                parseFloat(scheme[key]) >= 0 ? 'text-green-600' : 'text-red-600'
                              }`}
                            >
                              {parseFloat(scheme[key] || 0).toFixed(2)}%
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Basic Scheme Information */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-100 to-indigo-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">Scheme Information</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 border-r border-slate-200 sticky left-0 bg-slate-50 w-48">
                          Field
                        </th>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <th key={idx} className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                            <div className="flex flex-col items-center gap-1">
                              {scheme.scheme_category === 'AIF' && (
                                <div style={{
                                  border: '1.5px solid #6366f1',
                                  borderRadius: '6px',
                                  padding: '2px 6px',
                                  fontSize: '9px',
                                  fontWeight: '800',
                                  color: '#6366f1',
                                  letterSpacing: '0.5px'
                                }}>
                                  AIF
                                </div>
                              )}
                              <div className="text-xs font-normal text-slate-600">{scheme.scheme_name}</div>
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Fund Manager Name(s)
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                            {scheme.fund_manager || '-'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Fund Manager Bio
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center max-w-xs">
                            {scheme.fund_manager_bio || '-'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Inception Date
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                            {scheme.inception_date || '-'}
                          </td>
                        ))}
                      </tr>
                      <tr className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Edge in Investing
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center max-w-xs">
                            {scheme.edge_in_investing || '-'}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Fee Structure Comparison Table */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-green-100 to-emerald-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">Fee Structure Comparison</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b border-slate-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-sm font-semibold text-slate-700 border-r border-slate-200">
                          Scheme
                        </th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 1</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 2</th>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 w-32">Structure 3</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amcDetailsSchemes.map((scheme, schemeIdx) => (
                        <React.Fragment key={scheme.scheme_name}>
                          {/* Scheme Name Row */}
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <td colSpan="4" className="px-4 py-2 text-sm font-semibold text-slate-900">
                              {selectedAmcForDetails} - {scheme.scheme_name}
                            </td>
                          </tr>
                          {/* Management Fee */}
                          <tr className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                              Management Fee (%)
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_1_management ? `${scheme.fee_structure_1_management}%` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_2_management ? `${scheme.fee_structure_2_management}%` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_3_management ? `${scheme.fee_structure_3_management}%` : '-'}
                            </td>
                          </tr>
                          {/* Hurdle Rate */}
                          <tr className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                              Hurdle Rate (%)
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_1_hurdle ? `${scheme.fee_structure_1_hurdle}%` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_2_hurdle ? `${scheme.fee_structure_2_hurdle}%` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_3_hurdle ? `${scheme.fee_structure_3_hurdle}%` : '-'}
                            </td>
                          </tr>
                          {/* Performance Fee */}
                          <tr className="border-b border-slate-100 hover:bg-slate-50">
                            <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                              Performance Fee (%)
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_1_performance ? `${scheme.fee_structure_1_performance}%` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_2_performance ? `${scheme.fee_structure_2_performance}%` : '-'}
                            </td>
                            <td className="px-4 py-3 text-sm text-slate-800 text-center">
                              {scheme.fee_structure_3_performance ? `${scheme.fee_structure_3_performance}%` : '-'}
                            </td>
                          </tr>
                          {/* Exit Load (common for all) */}
                          <tr className={schemeIdx === amcDetailsSchemes.length - 1 ? "" : "border-b-2 border-slate-300 hover:bg-slate-50"}>
                            <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 pl-8">
                              Exit Load (%)
                            </td>
                            <td colSpan="3" className="px-4 py-3 text-sm text-slate-800 text-center font-semibold">
                              {scheme.exit_load ? `${scheme.exit_load}%` : '-'}
                            </td>
                          </tr>
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Other Details Table */}
              <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
                <div className="bg-gradient-to-r from-purple-100 to-pink-50 px-4 py-3 border-b border-slate-200">
                  <h3 className="text-lg font-semibold text-slate-800">Other Details</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-center text-sm font-semibold text-slate-700 border-r border-slate-200 sticky left-0 bg-slate-50 w-48">
                          Field
                        </th>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <th key={idx} className="px-4 py-3 text-center text-sm font-semibold text-slate-700">
                            <div className="font-medium text-slate-900">{selectedAmcForDetails}</div>
                            <div className="text-xs font-normal text-slate-600 mt-1">{scheme.scheme_name}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Type of Fund
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                            {scheme.fund_type || '-'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Size of Fund
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                            {scheme.fund_size || '-'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          AUM (Cr)
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                            {scheme.aum && scheme.aum !== '0' ? scheme.aum : 'N/A'}
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Presentation Link
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3">
                            <div className="flex justify-center">
                              {scheme.presentation_link ? (
                                <a 
                                  href={scheme.presentation_link}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-teal-600 hover:text-teal-700 text-sm flex items-center gap-1 font-medium"
                                >
                                  View Link
                                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                  </svg>
                                </a>
                              ) : (
                                '-'
                              )}
                            </div>
                          </td>
                        ))}
                      </tr>
                      <tr className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Sales Contact Email
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                            {scheme.sales_email || '-'}
                          </td>
                        ))}
                      </tr>
                      <tr className="hover:bg-slate-50">
                        <td className="px-4 py-3 text-sm font-medium text-slate-600 border-r border-slate-200 sticky left-0 bg-white text-center">
                          Sales Contact Mobile
                        </td>
                        {amcDetailsSchemes.map((scheme, idx) => (
                          <td key={idx} className="px-4 py-3 text-sm text-slate-800 text-center">
                            {scheme.sales_mobile || '-'}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        );
      })()}
    </div>
  );
};

export default AdminScraper;
